import './InputTable.scss';
import {
  Fragment,
  ReactElement,
  useEffect,
  useMemo,
  useState,
  ClipboardEvent,
  ReactNode
} from 'react';
import { MdDelete, MdEdit, MdSettings } from 'react-icons/md';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Design, Placement, Size } from '@Components/designConstants';
import {
  ControlVariableTable,
  IncrementalityTestingEntry,
  IncrementalityTestingTable,
  MediaEntry,
  MediaTable,
  OfflineInputsEntry,
  OfflineInputsTable,
  OfflineInputTabs,
  OutcomeDataTable
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { isBefore } from 'date-fns';
import {
  capitalizeFirstLetter,
  formatDateInUTC,
  formatMoney,
  formatNumber,
  formatPercent
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { HiOutlinePlus } from 'react-icons/hi';
import { TableInputField } from './TableInputField';
import {
  buildBlankControlVariableEntries,
  buildBlankIncrementalityTestingEntries,
  buildBlankOutcomeDataEntries,
  buildMediaTableEditModeBlanks,
  ControlVariableUnitTypes,
  createEmptyKpi,
  EntryEditsMap,
  hasRowId,
  IncrementalityTestingEntryEditsMap,
  MediaEntryWithRowId,
  parsePastedMediaEntryRows
} from './OfflineInputsUtils';
import { DeleteConfirmationDialog } from './OfflineInputsDialogs';
import { SetError } from '../../../redux/modals';

const renderWithBreakableUnderscores = (value: string) => {
  const segments = value.split('_');
  return segments.map((segment, i) => (
    <Fragment key={i}>
      {segment}
      {i < segments.length - 1 && '_'}
      {i < segments.length - 1 && <wbr />}
    </Fragment>
  ));
};

interface InputTableHeaderProps {
  name: string;
  description: string;
}

export const InputTableHeader = ({ name, description }: InputTableHeaderProps) => (
  <div className='header-labels breakable'>
    <div className='top-row'>
      <strong>{renderWithBreakableUnderscores(name)}</strong>
      <div className='tooltip-container'>
        <InfoTooltip className='incrementality-testing-table-tooltip' placement={Placement.TOP}>
          <div className='tooltip-text'>{description}</div>
        </InfoTooltip>
      </div>
    </div>
  </div>
);

// Problem: each BPMTable has the same flex basis, BUT they can have a variable number of columns
// For Offline Inputs, we want each column to be the same width, without modifying BPMTable's other usages
// So we put each table in its own container at the desired width
// AND we put a container around all of the table with the margins accounted for as well

// Incrementality Testing does not contain a date in the same way other Offline Inputs do.
// Conditions are made throughout the code to support Incrementality Testing alongside other Offline Inputs.

// Desired width for each column in Offline Inputs
export const INPUT_COLUMN_WIDTH = 125;

export const getNumColumnsFromTable = (
  inputType: keyof typeof OfflineInputTabs,
  table: OfflineInputsTable | IncrementalityTestingTable
): number => {
  switch (OfflineInputTabs[inputType]) {
    case OfflineInputTabs.PAID_MEDIA_INPUTS:
    case OfflineInputTabs.NON_PAID_MEDIA_INPUTS: {
      const mediaTable = table as MediaTable;
      // Date always present
      let numColumns = 1;
      // spend column
      if (OfflineInputTabs[inputType] === OfflineInputTabs.PAID_MEDIA_INPUTS) {
        numColumns++;
      }
      // impressions column
      if (mediaTable.hasImpressions) {
        numColumns++;
      }
      // redemptions column
      if (mediaTable.hasRedemptions) {
        numColumns++;
      }
      // other unit column
      if (mediaTable.otherUnitLabel) {
        numColumns++;
      }
      // dma column
      if (mediaTable.hasDma) {
        numColumns++;
      }
      return numColumns;
    }
    case OfflineInputTabs.OUTCOME_DATA:
    case OfflineInputTabs.CONTROL_VARIABLES:
      // Outcome data & Control Variables always have a date and a volume
      return 2;
    case OfflineInputTabs.INCREMENTALITY_TESTING:
      // Incrementality Testing tables display: kpiName, spend, incrementalVolume, nControl, nTreatment, lowerBound, upperBound, confidenceLevel
      return 8;
    default:
      return 0;
  }
};

export const getTablesContainerInnerWidth = (
  inputType: keyof typeof OfflineInputTabs,
  tables: OfflineInputsTable[] | IncrementalityTestingTable[]
): { width: string } => {
  const columnsLength = (tables as (OfflineInputsTable | IncrementalityTestingTable)[]).reduce(
    (acc, table) => acc + getNumColumnsFromTable(inputType, table),
    0
  );

  const desiredTableWidth = INPUT_COLUMN_WIDTH * columnsLength;
  // There are 32px in margins between each table
  const desiredPaddingWidth = (tables.length - 1) * 32;

  return {
    width: `${desiredTableWidth + desiredPaddingWidth}px`
  };
};

const isNil = (value: unknown): boolean => value === null || value === undefined;

// Module-level so the "sorting suppressed" case hands BPMTable a stable array identity every
// render — a fresh `[]` literal would bust the tableHeaders memo (and BPMTable's own memos) on
// every keystroke.
const NO_SORT: { ascending: boolean; index: number }[] = [];

const renderNoRows = () => <div>No data to show</div>;

const buildInputHeader = (
  entries: OfflineInputsEntry[] | IncrementalityTestingEntry[],
  onPaste: (
    e: ClipboardEvent<HTMLInputElement>,
    startRow: number,
    startCol: number,
    columnsOrder,
    setError,
    sortParamList
  ) => void,
  columnIndex: number,
  isEdit: boolean,
  columnsOrder: string[],
  inputName: string,
  inputLabel: string,
  edits: EntryEditsMap | IncrementalityTestingEntryEditsMap,
  setEdits: (edits: EntryEditsMap | IncrementalityTestingEntryEditsMap) => void,
  setError,
  sortParamList,
  toElementId?,
  display: (data: any) => ReactElement = data => (
    <span id={toElementId ? toElementId(data) : undefined}>{data}</span>
  ),
  valueType = 'number',
  getDataTestId?: (data: any) => string
) => ({
  label: inputLabel,
  name: inputName,
  flex: 1,
  renderer: (data): ReactElement => {
    const editKey = getEditKey(data);
    const rowIndex = entries.indexOf(data);
    const editData = edits[editKey];
    const isBinary = inputLabel === capitalizeFirstLetter(ControlVariableUnitTypes.BINARY);

    return isEdit ? (
      <TableInputField
        elementId={toElementId ? toElementId(data) : undefined}
        dataTestId={getDataTestId ? getDataTestId(data) : undefined}
        type={valueType}
        value={editData ? editData[inputName] : data[inputName]}
        onChange={e => {
          // Every other column treats a cleared field as `undefined` ("not entered"). The date
          // column is the exception: '' is the sentinel that marks a hasDma table's blank capture
          // row (see buildBlankDmaEntryRow), and the self-healing effect / addBlankDmaRow guard
          // both test for `=== ''`. Clearing a typed date to `undefined` would orphan the row —
          // no longer recognized as the capture row, so a second one gets added, and the orphan
          // then blocks save with `Invalid date "undefined"`.
          const clearedValue = inputName === 'date' ? '' : undefined;
          const newValue = e.target.value === '' ? clearedValue : e.target.value;
          if (
            isBinary &&
            Number.parseInt(newValue ?? '', 10) !== 0 &&
            Number.parseInt(newValue ?? '', 10) !== 1
          ) {
            return;
          }
          const currentEdit = edits[editKey];

          // Mutating the edits map in place then dispatching a copy is the intended
          // contract of this helper (callers pass the map with its setter).
          if (currentEdit) {
            // eslint-disable-next-line no-param-reassign
            edits[editKey] = { ...currentEdit, [inputName]: newValue };
          } else {
            // eslint-disable-next-line no-param-reassign
            edits[editKey] = { ...data, [inputName]: newValue };
          }

          setEdits({ ...edits });
        }}
        onPaste={e => onPaste(e, rowIndex, columnIndex, columnsOrder, setError, sortParamList)}
      />
    ) : (
      display(data[inputName])
    );
  }
});

const getEditKey = (entry: OfflineInputsEntry | IncrementalityTestingEntry): string => {
  if ('entryId' in entry) {
    return entry.entryId;
  }
  if (hasRowId(entry)) {
    return entry.rowId;
  }
  return entry.date;
};

const buildTableHeaders = (
  inputType: keyof typeof OfflineInputTabs,
  table: OfflineInputsTable | IncrementalityTestingTable,
  entries: OfflineInputsEntry[] | IncrementalityTestingEntry[],
  edits: EntryEditsMap | IncrementalityTestingEntryEditsMap,
  setEdits: (edits: EntryEditsMap | IncrementalityTestingEntryEditsMap) => void,
  setError: SetError,
  isEdit = false,
  sortParamList: { ascending: boolean; index: number }[] = [],
  newEntriesRef: (OfflineInputsEntry | IncrementalityTestingEntry)[] = [],
  deleteRowCallback?: (tableId: string, rowId: string) => void,
  addPastedDmaRows?: (tableId: string, rows: MediaEntryWithRowId[]) => void
) => {
  const onPaste = (
    e: ClipboardEvent,
    startRow: number,
    startCol: number,
    pasteColumnsOrder: string[],
    pasteSetError: SetError,
    pasteSortParamList: { ascending: boolean; index: number }[] = []
  ) => {
    e.preventDefault();
    // Catch bogus inputs and early exit
    if (startRow < 0 || startCol < 0) {
      return;
    }

    const pasteMediaTable = table as MediaTable;
    const isHasDmaPasteTable =
      (OfflineInputTabs[inputType] === OfflineInputTabs.PAID_MEDIA_INPUTS ||
        OfflineInputTabs[inputType] === OfflineInputTabs.NON_PAID_MEDIA_INPUTS) &&
      Boolean(pasteMediaTable.hasDma);
    const captureEntry = entries[startRow] as MediaEntry | undefined;
    // Growable paste is specifically a "paste starting from Date" gesture — pasting into any
    // other column of the capture row (e.g. Dollars) falls through to the normal
    // positional-overwrite logic below, filling just that one cell.
    const pastedIntoCaptureRow = isHasDmaPasteTable && captureEntry?.date === '' && startCol === 0;

    if (pastedIntoCaptureRow && captureEntry) {
      const clipboardData = e.clipboardData.getData('text/plain');
      const parsedRows = parsePastedMediaEntryRows(clipboardData, pasteColumnsOrder);
      if (parsedRows.length === 0) {
        return;
      }

      const [firstRow, ...restRows] = parsedRows;
      const { rowId: _discardedRowId, ...firstRowFields } = firstRow;
      const captureKey = getEditKey(captureEntry);
      // eslint-disable-next-line no-param-reassign
      edits[captureKey] = { ...captureEntry, ...firstRowFields };
      setEdits({ ...edits });

      // A pasted line with a blank Date column would otherwise be indistinguishable from the
      // real capture row (also date === ''), suppressing it — drop those before appending.
      const newRestRows = restRows.filter(row => row.date !== '');
      if (newRestRows.length > 0 && addPastedDmaRows) {
        addPastedDmaRows(table.tableId, newRestRows);
      }
      return;
    }

    // for Incrementality Testing, no date & thus no sort validation. paste ascending.
    const isIncrementalityTesting =
      OfflineInputTabs[inputType] === OfflineInputTabs.INCREMENTALITY_TESTING;

    // We will only handle onPaste if the table is sorted by date
    let ascending = false;
    if (isHasDmaPasteTable) {
      // hasDma tables are deliberately left unsorted while editing (see effectiveSortParamList
      // in InputTable) so that live re-sorting can't rebind a focused input mid-keystroke.
      // "Unsorted" here means array/insertion order, which reads top-to-bottom on screen — so
      // the positional fall-through loop below must walk forward, and the "sorted by Date"
      // requirement no longer applies.
      ascending = true;
    } else if (!isIncrementalityTesting && pasteSortParamList.length > 0) {
      const [firstSortParam] = pasteSortParamList;
      if (firstSortParam.index !== 0) {
        pasteSetError({ message: 'Pasting only supported when table is sorted by Date.' });
        return;
      }

      ({ ascending } = firstSortParam);
    }

    const clipboardData = e.clipboardData.getData('text/plain');
    const pastedRows: string[][] = clipboardData.split('\n').map(row => row.split('\t'));
    for (
      let i = 0;
      (isIncrementalityTesting || ascending ? startRow + i < entries.length : startRow - i >= 0) &&
      i < pastedRows.length;
      i++
    ) {
      const tableRow = entries[isIncrementalityTesting || ascending ? startRow + i : startRow - i];
      const editKey = getEditKey(tableRow);
      let editRow = edits[editKey];
      const pastedRow = pastedRows[i];

      if (!editRow) {
        // Callers pass the edits map together with its setter; mutating in place
        // then dispatching a copy is the intended contract of this helper.
        // eslint-disable-next-line no-param-reassign
        edits[editKey] = tableRow;
        editRow = edits[editKey];
      }

      for (let j = 0; j + startCol < pasteColumnsOrder.length && j < pastedRow.length; j++) {
        const colName = pasteColumnsOrder[j + startCol];
        if (colName === 'dma') {
          // DMA accepts text values ("canada" or a numeric code string) — store as-is
          editRow[colName] = pastedRow[j].trim() || undefined;
        } else if (pastedRow[j] === '') {
          // Pasted blank cells become 0s for Outcome Data / Control Variables. Media Inputs and
          // Incrementality Testing leave them undefined so a blank stays distinguishable from a
          // confirmed zero (DEV-21622) instead of silently becoming one.
          const zeroFillsBlanks = ![
            OfflineInputTabs.INCREMENTALITY_TESTING,
            OfflineInputTabs.PAID_MEDIA_INPUTS,
            OfflineInputTabs.NON_PAID_MEDIA_INPUTS
          ].includes(OfflineInputTabs[inputType]);
          editRow[colName] = zeroFillsBlanks ? 0 : undefined;
        } else {
          // [^\d.] matches any character that is not a digit or .
          const filteredInput = pastedRow[j].replaceAll(/[^\d.]/g, '');
          if (!Number.isNaN(Number(filteredInput))) {
            editRow[colName] = Number(filteredInput);
          }
        }

        // Do nothing with non-numeric, non-blank inputs (non-DMA columns)
      }
    }

    setEdits({ ...edits });
  };

  const tableHeaders: Header[] = [];
  const columnsOrder: string[] = [];

  const buildInputHeaderWithDefaults = (
    name,
    label,
    display?,
    type?,
    toElementId?,
    getDataTestId?
  ) =>
    buildInputHeader(
      entries,
      onPaste,
      tableHeaders.length,
      isEdit,
      columnsOrder,
      name,
      label,
      edits,
      setEdits,
      setError,
      sortParamList,
      toElementId,
      display,
      type,
      getDataTestId
    );

  const dateOwnerMediaTable = table as MediaTable;
  const dateIsEditable =
    (OfflineInputTabs[inputType] === OfflineInputTabs.PAID_MEDIA_INPUTS ||
      OfflineInputTabs[inputType] === OfflineInputTabs.NON_PAID_MEDIA_INPUTS) &&
    Boolean(dateOwnerMediaTable.hasDma);

  // The Date column must be the first thing pushed (columnIndex 0) so that every column
  // pushed by the switch below stays in sync with columnsOrder's paste-index positions —
  // unshifting the header after the switch (as the non-editable case still does) would
  // desync every other column's captured paste index from columnsOrder.
  if (dateIsEditable) {
    tableHeaders.push(
      buildInputHeaderWithDefaults(
        'date',
        'Date (Day)',
        dateStr => (dateStr ? formatDateInUTC(new Date(dateStr)) : ''),
        'text',
        data => `${table.tableId}_${getEditKey(data)}`,
        // The capture row is whichever row's (edit-merged) date is still blank — at most one
        // at a time, enforced by addBlankDmaRow's guard. A stable, non-random test hook: every
        // other id on this row is a random client-side rowId, unusable as a Cypress selector.
        data => (data.date === '' ? 'dma-capture-date-input' : 'dma-row-date-input')
      )
    );
    columnsOrder.push('date');
  }

  switch (OfflineInputTabs[inputType]) {
    case OfflineInputTabs.PAID_MEDIA_INPUTS:
    case OfflineInputTabs.NON_PAID_MEDIA_INPUTS: {
      const mediaTable = table as MediaTable;
      const { isPaid, hasImpressions, otherUnitLabel, hasRedemptions, hasDma } = mediaTable;

      if (isPaid) {
        tableHeaders.push(
          buildInputHeaderWithDefaults('spend', 'Dollars', spendStr =>
            isNil(spendStr) ? '' : formatMoney(Number(spendStr))
          )
        );
        columnsOrder.push('spend');
      }

      if (hasImpressions) {
        tableHeaders.push(
          buildInputHeaderWithDefaults('impressions', 'Imps', impsStr =>
            isNil(impsStr) ? '' : formatNumber(Number(impsStr))
          )
        );
        columnsOrder.push('impressions');
      }

      if (otherUnitLabel) {
        const otherUnitLabelCapitalized = `${otherUnitLabel
          .charAt(0)
          .toUpperCase()}${otherUnitLabel.slice(1)}`;

        tableHeaders.push(buildInputHeaderWithDefaults('otherUnit', otherUnitLabelCapitalized));
        columnsOrder.push('otherUnit');
      }

      if (hasRedemptions) {
        tableHeaders.push(buildInputHeaderWithDefaults('redemptions', 'Redemptions'));
        columnsOrder.push('redemptions');
      }

      if (hasDma) {
        tableHeaders.push(buildInputHeaderWithDefaults('dma', 'DMA', undefined, 'text'));
        columnsOrder.push('dma');
      }

      if (hasDma && isEdit) {
        const newEntryRowIds = new Set(
          (newEntriesRef as OfflineInputsEntry[]).filter(hasRowId).map(entry => entry.rowId)
        );
        tableHeaders.push({
          label: '',
          name: 'delete',
          flex: false,
          width: 48,
          nonFilterable: true,
          nonInteractive: true,
          renderer: (data): ReactElement => {
            if (!newEntryRowIds.has(getEditKey(data))) {
              return <span />;
            }
            return (
              <IconButton
                design={Design.NEUTRAL}
                data-testid='delete-row'
                onClick={() => deleteRowCallback?.(table.tableId, getEditKey(data))}
              >
                <MdDelete />
              </IconButton>
            );
          }
        });
      }
      break;
    }
    case OfflineInputTabs.OUTCOME_DATA: {
      const outcomeDataTable = table as OutcomeDataTable;
      tableHeaders.push(
        buildInputHeaderWithDefaults(
          'volume',
          outcomeDataTable.unitType === 'Dollars' ? 'Dollars' : 'Count/Volume',
          outcomeDataTable.unitType === 'Dollars'
            ? dollarsStr => formatMoney(Number(dollarsStr))
            : countStr => formatNumber(Number(countStr))
        )
      );
      columnsOrder.push('volume');
      break;
    }
    case OfflineInputTabs.CONTROL_VARIABLES: {
      const controlVariableTable = table as ControlVariableTable;
      const getControlVariableLabel = (): string => {
        if (controlVariableTable.unitType === ControlVariableUnitTypes.PERCENTAGE) {
          return 'Percent';
        }
        if (controlVariableTable.unitType === ControlVariableUnitTypes.BINARY) {
          return capitalizeFirstLetter(ControlVariableUnitTypes.BINARY);
        }
        return controlVariableTable.unitType
          .split(' ')
          .map(word => capitalizeFirstLetter(word))
          .join(' ');
      };
      tableHeaders.push(
        buildInputHeaderWithDefaults(
          'volume',
          getControlVariableLabel(),
          controlVariableTable.unitType === ControlVariableUnitTypes.PERCENTAGE
            ? percentageStr => formatPercent(Number(percentageStr), 1)
            : binaryOrOtherStr => formatNumber(Number(binaryOrOtherStr))
        )
      );
      columnsOrder.push('volume');
      break;
    }
    case OfflineInputTabs.INCREMENTALITY_TESTING: {
      tableHeaders.push(
        buildInputHeaderWithDefaults(
          'kpiName',
          'Outcome Variable',
          undefined,
          'text',
          data => `${data.entryId}`
        )
      );
      columnsOrder.push('kpiName');
      tableHeaders.push(
        buildInputHeaderWithDefaults('spend', 'Spend', spendStr => formatMoney(Number(spendStr)))
      );
      columnsOrder.push('spend');
      tableHeaders.push(
        buildInputHeaderWithDefaults('incrementalVolume', 'iVolume', vol => formatNumber(vol, 2))
      );
      columnsOrder.push('incrementalVolume');
      tableHeaders.push(
        buildInputHeaderWithDefaults('nControl', 'N Control', n =>
          isNil(n) ? '' : formatNumber(n, 0)
        )
      );
      columnsOrder.push('nControl');
      tableHeaders.push(
        buildInputHeaderWithDefaults('nTreatment', 'N Treatment', n =>
          isNil(n) ? '' : formatNumber(n, 0)
        )
      );
      columnsOrder.push('nTreatment');
      tableHeaders.push(
        buildInputHeaderWithDefaults('lowerBound', 'Lower Bound', l =>
          isNil(l) ? '' : formatNumber(l, 2)
        )
      );
      columnsOrder.push('lowerBound');
      tableHeaders.push(
        buildInputHeaderWithDefaults('upperBound', 'Upper Bound', u =>
          isNil(u) ? '' : formatNumber(u, 2)
        )
      );
      columnsOrder.push('upperBound');
      tableHeaders.push(
        buildInputHeaderWithDefaults('confidenceLevel', 'Confidence Level', pct =>
          isNil(pct) ? '' : formatPercent(pct, 2)
        )
      );
      columnsOrder.push('confidenceLevel');

      break;
    }
    default:
      break;
  }

  if (OfflineInputTabs[inputType] !== OfflineInputTabs.INCREMENTALITY_TESTING && !dateIsEditable) {
    tableHeaders.unshift({
      label: 'Date (Day)',
      name: 'date',
      flex: 1,
      renderer: (data): ReactElement => (
        <span id={`${table.tableId}_${data.date.split('T')[0]}`}>
          {formatDateInUTC(new Date(data.date))}
        </span>
      )
    });
  }

  // A hasDma table is deliberately unsorted while editing, so its headers must not be clickable —
  // a sort applied mid-edit would re-sort rows live and rebind the focused input to a different
  // row on the next keystroke, which is the whole problem the suppression exists to avoid.
  if (dateIsEditable && isEdit) {
    return tableHeaders.map(header => ({ ...header, nonInteractive: true }));
  }

  return tableHeaders;
};

interface InputTableProps<
  TTable extends OfflineInputsTable | IncrementalityTestingTable,
  TEntry extends OfflineInputsEntry | IncrementalityTestingEntry,
  TEntryEditsMap extends EntryEditsMap | IncrementalityTestingEntryEditsMap
> {
  inputType: keyof typeof OfflineInputTabs;
  table: TTable;
  deleteTable: (tableId: string) => void;
  tablesInEditMode: string[];
  startEditingTable: (tableId: string, blanks: TEntry[]) => void;
  editingUnlocked: boolean;
  edits: TEntryEditsMap;
  setEdits: (edits: TEntryEditsMap) => void;
  newEntries: TEntry[];
  submitting: boolean;
  setError: SetError;
  filter?: (entry: any) => boolean;
  headerLabels: ReactNode;
  addIncrementalityTestingKpi?: (tableId: string, blanks: IncrementalityTestingEntry[]) => void;
  editTableSettings?: (tableId: string) => void;
  addBlankDmaRow?: (tableId: string) => void;
  addPastedDmaRows?: (tableId: string, rows: MediaEntryWithRowId[]) => void;
  deleteUncommittedEntry?: (tableId: string, rowId: string) => void;
}

export const InputTable = <
  TTable extends OfflineInputsTable | IncrementalityTestingTable,
  TEntry extends OfflineInputsEntry | IncrementalityTestingEntry,
  TEntryEditsMap extends EntryEditsMap | IncrementalityTestingEntryEditsMap
>({
  inputType,
  table,
  deleteTable,
  tablesInEditMode,
  startEditingTable,
  editingUnlocked,
  edits,
  setEdits,
  newEntries,
  submitting,
  setError,
  filter,
  headerLabels,
  addIncrementalityTestingKpi,
  editTableSettings,
  addBlankDmaRow,
  addPastedDmaRows,
  deleteUncommittedEntry
}: InputTableProps<TTable, TEntry, TEntryEditsMap>): ReactElement => {
  const { tableId, entries } = table;
  const isHasDmaMediaTable =
    (OfflineInputTabs[inputType] === OfflineInputTabs.PAID_MEDIA_INPUTS ||
      OfflineInputTabs[inputType] === OfflineInputTabs.NON_PAID_MEDIA_INPUTS) &&
    Boolean((table as MediaTable).hasDma);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sortParamList, setSortParamList] = useState<{ ascending: boolean; index: number }[]>(
    OfflineInputTabs[inputType] !== OfflineInputTabs.INCREMENTALITY_TESTING
      ? [
          {
            ascending: true,
            index: 0
          }
        ]
      : []
  );
  const isEdit = tablesInEditMode.includes(tableId);

  // StickyTable keys its rendered cells by grid position, not row identity. A live-sorted table
  // therefore rebinds the focused <input> to whatever row moved into that slot — so typing a
  // date into a hasDma table (where the blank capture row sorts to index 0 and jumps the moment
  // a character lands) was impossible, every keystroke effectively resetting. Suppressing the
  // sort while editing keeps array order == visual order: the capture row stays at the bottom
  // in insertion order, and multi-line paste again writes into visually adjacent rows.
  // `sortParamList` state itself is left intact so the sort can be restored on exiting edit mode.
  const sortSuppressed = isHasDmaMediaTable && isEdit;
  const effectiveSortParamList = sortSuppressed ? NO_SORT : sortParamList;

  // Edit-merged but *unfiltered* — kept separate from entriesToUse below so the self-healing
  // effect can check for a blank capture row without being fooled by BPMTable's FilterBar
  // (whose token persists in localStorage across sessions and could otherwise hide every
  // date === '' row, see the effect below).
  const mergedEntries = useMemo(
    () =>
      [...entries, ...newEntries].map(entry => {
        const editKey = getEditKey(entry);
        const edit = edits[editKey];
        if (edit) {
          return {
            ...entry,
            ...edit
          };
        }
        return entry;
      }),
    [edits, entries, newEntries]
  );

  const entriesToUse = useMemo(() => {
    if (filter) {
      return mergedEntries.filter(entry => {
        if ('date' in entry) {
          const formattedEntry = { ...entry, date: formatDateInUTC(new Date(entry.date)) };
          return filter(formattedEntry);
        }
        // For IncrementalityTestingEntry, no date formatting needed
        return filter(entry);
      });
    }

    return mergedEntries;
  }, [mergedEntries, filter]);

  const tableHeaders = useMemo(
    () =>
      buildTableHeaders(
        inputType,
        table,
        entriesToUse as OfflineInputsEntry[] | IncrementalityTestingEntry[],
        edits,
        setEdits as (edits: EntryEditsMap | IncrementalityTestingEntryEditsMap) => void,
        setError,
        isEdit,
        effectiveSortParamList,
        newEntries,
        deleteUncommittedEntry,
        addPastedDmaRows
      ),
    [
      inputType,
      table,
      entriesToUse,
      edits,
      setEdits,
      setError,
      isEdit,
      effectiveSortParamList,
      newEntries,
      deleteUncommittedEntry,
      addPastedDmaRows
    ]
  );

  useEffect(() => {
    if (!isEdit || !isHasDmaMediaTable || !addBlankDmaRow) {
      return;
    }
    // Check mergedEntries (edit-merged, unfiltered) rather than entriesToUse: entriesToUse
    // applies the FilterBar's filter, which persists in localStorage across sessions — a
    // leftover filter that hides every date === '' row would otherwise make this effect never
    // find the capture row it just added, causing it to keep calling addBlankDmaRow forever.
    // Checking the raw entries/newEntries arrays instead (without the edit merge) would have
    // the opposite problem: an edited-but-still-raw capture row's date never changes in the
    // array itself (edits live only in the separate edits map), so it would look permanently
    // blank and a second capture row would never get added after the first is filled in.
    const hasBlankCaptureRow = mergedEntries.some(
      entry => 'date' in entry && (entry as MediaEntry).date === ''
    );
    if (!hasBlankCaptureRow) {
      addBlankDmaRow(tableId);
    }
  }, [isEdit, isHasDmaMediaTable, mergedEntries, tableId, addBlankDmaRow]);

  const buildBlankEntriesWithRecent = () => {
    // Safeguard against empty tables
    if (
      entries.length < 1 ||
      OfflineInputTabs[inputType] === OfflineInputTabs.INCREMENTALITY_TESTING
    ) {
      return [];
    }

    const typedEntries = entries as OfflineInputsEntry[];
    const latestEntry = typedEntries.reduce((acc, entry) => {
      const dateFromEntry = new Date(entry.date);
      return isBefore(dateFromEntry, acc) ? acc : dateFromEntry;
    }, new Date(typedEntries[0]?.date));

    latestEntry.setDate(latestEntry.getDate() + 1);

    switch (OfflineInputTabs[inputType]) {
      case OfflineInputTabs.PAID_MEDIA_INPUTS:
      case OfflineInputTabs.NON_PAID_MEDIA_INPUTS:
        return buildMediaTableEditModeBlanks(table as MediaTable, latestEntry);
      case OfflineInputTabs.OUTCOME_DATA: {
        const outcomeDataTable = table as OutcomeDataTable;
        if (outcomeDataTable.hasEndDate) {
          return [];
        }
        return buildBlankOutcomeDataEntries(latestEntry);
      }
      case OfflineInputTabs.CONTROL_VARIABLES: {
        const controlVariableTable = table as ControlVariableTable;
        if (controlVariableTable.hasEndDate) {
          return [];
        }
        return buildBlankControlVariableEntries(latestEntry);
      }
      default:
        return [];
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        show={showDeleteDialog}
        onDelete={() => deleteTable(tableId)}
        onHide={() => setShowDeleteDialog(false)}
      />
      <div
        data-testid={isHasDmaMediaTable ? 'dma-table' : undefined}
        style={{ width: `${INPUT_COLUMN_WIDTH * getNumColumnsFromTable(inputType, table)}px` }}
      >
        <BPMTable
          // useSortHeader seeds its internal sort state from `overrideSortParamList` via
          // `useState(...)` — i.e. at mount only, so later prop changes are ignored. Keying the
          // table on the suppression state remounts it at each edit-mode boundary, which is what
          // actually makes `effectiveSortParamList` take effect (and clears whatever column the
          // user had sorted by before entering edit mode). Non-hasDma tables get a constant key,
          // so they never remount and their behavior is unchanged.
          key={sortSuppressed ? 'sort-suppressed' : 'sorted'}
          data={entriesToUse}
          filterBar={false}
          headers={tableHeaders}
          superHeaderHeight={72}
          noRowsRenderer={renderNoRows}
          additionalControls={
            <div className='super-header'>
              <div className='header-controls'>
                {editingUnlocked &&
                  isEdit &&
                  OfflineInputTabs[inputType] === OfflineInputTabs.INCREMENTALITY_TESTING &&
                  addIncrementalityTestingKpi && (
                    <Button
                      size={Size.LG}
                      id='add-incrementality-test-kpi'
                      design={Design.NEUTRAL}
                      disabled={submitting}
                      onClick={() =>
                        addIncrementalityTestingKpi(
                          tableId,
                          buildBlankIncrementalityTestingEntries([createEmptyKpi()])
                        )
                      }
                    >
                      <HiOutlinePlus />
                      KPI
                    </Button>
                  )}
                {editingUnlocked && !isEdit && (
                  <Button
                    size={Size.LG}
                    design={Design.NEUTRAL}
                    disabled={submitting}
                    data-testid='start-edit-table'
                    onClick={() =>
                      startEditingTable(tableId, buildBlankEntriesWithRecent() as TEntry[])
                    }
                  >
                    <MdEdit />
                  </Button>
                )}
                {editingUnlocked && !isEdit && editTableSettings && (
                  <Button
                    size={Size.LG}
                    design={Design.NEUTRAL}
                    disabled={submitting}
                    onClick={() => editTableSettings(tableId)}
                  >
                    <MdSettings />
                  </Button>
                )}
                {editingUnlocked && (
                  <Button
                    size={Size.LG}
                    design={Design.NEUTRAL}
                    disabled={submitting}
                    onClick={() => {
                      setShowDeleteDialog(true);
                    }}
                  >
                    <MdDelete />
                  </Button>
                )}
              </div>
              {headerLabels}
            </div>
          }
          onUpdateSortParamList={newSortParamList => {
            // While suppressed, useSortHeader mounts with `[]` and immediately echoes that back
            // up. Recording it would erase the date sort the table has to return to when edit
            // mode exits, so ignore the echo — nothing can legitimately change the sort while
            // suppressed anyway (every header is nonInteractive, see buildTableHeaders).
            if (sortSuppressed) {
              return;
            }
            setSortParamList(newSortParamList);
          }}
          overrideSortParamList={effectiveSortParamList}
        />
      </div>
    </>
  );
};
